// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import * as Slice from "../slice"
import type { RootState } from "../../../app/store"
import { showSnackBar } from "../../snackbar/actions"
import {
  ListTrainingGroupsRequestSchema,
  ListTrainingGroupsResponse,
} from "../../../generated/wisetime/user/assistant/v4/preferences_pb"
import { executor, Init, narratePreferencesService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"

type ListTrainingGroupsRpc = (request: Init<typeof ListTrainingGroupsRequestSchema>) => Observable<ListTrainingGroupsResponse>

export const listTrainingGroups = (listTrainingGroupsRpc: ListTrainingGroupsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listTrainingGroups.match),
      mergeMap(action => listTrainingGroupsRpc(action.payload.request).pipe(
        withLatestFrom(state$),
        mergeMap(([response, state]) => {
          const { trainingGroups } = response
          if (action.payload.replaceGroupId
            && trainingGroups.length === 0
            && action.payload.request.excludeTrainingGroupIds?.length !== 0
          ) {
            return concat([
              Slice.resetReplacedGroupIds(),
              Actions.listTrainingGroups({
                request: { count: action.payload.request.count },
                replaceGroupId: action.payload.replaceGroupId,
              }),
            ])
          }
          const actions: Action[] = []
          if (action.payload.replaceGroupId && trainingGroups[0]) {
            actions.push(Slice.setTrainingGroup({
              trainingGroup: trainingGroups[0],
              replaceGroupId: action.payload.replaceGroupId,
            }))
          } else {
            actions.push(Slice.setTrainingGroups(trainingGroups))
          }
          response.trainingGroups.forEach(group => {
            if (state.narratePreferences.previewNarratePreferences) {
              actions.push(Actions.getTrainingNarrative({
                trainingGroup: group,
                preferences: state.narratePreferences.previewNarratePreferences,
              }))
            } else if (state.narratePreferences.savedNarratePreferences) {
              actions.push(Actions.getTrainingNarrative({
                trainingGroup: group,
                preferences: state.narratePreferences.savedNarratePreferences,
              }))
            }
          })
          return concat(actions)
        }),
        catchError(_ => of(showSnackBar({
          message: t({
            id: "msg_list_training_groups_failed",
            message: "Failed to load timesheets. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
          }),
          messageType: "ERROR",
        }))),
      )),
    )

const listTrainingGroupsRpc = (request: Init<typeof ListTrainingGroupsRequestSchema>): Observable<ListTrainingGroupsResponse> =>
  executor(() => narratePreferencesService.listTrainingGroups(create(ListTrainingGroupsRequestSchema, request)))

export default listTrainingGroups(listTrainingGroupsRpc)
